import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { useState, useEffect, useMemo } from "react";
import {
  CheckCircle2, XCircle, Eye, Gavel, ArrowLeft, Search,
  Building2, MapPin, Clock, Loader2,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { roomTypesApi, venuesApi, resolveMediaUrl, type RoomTypeResponse, type VenueResponse, getStoredAuth } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatNaira } from "@/data/venues";

export const Route = createFileRoute("/admin/moderator")({
  beforeLoad: ({ location }) => {
    if (typeof window === "undefined") return;
    const auth = getStoredAuth();
    if (!auth) throw redirect({ to: "/login", search: { redirect: location.pathname } });
    if (!auth.role) throw redirect({ to: "/login", search: { redirect: location.pathname } });
    const allowed = ["super_admin", "moderator"];
    if (!allowed.includes(auth.role)) throw redirect({ to: "/admin/dashboard" });
  },
  component: ModeratorPage,
});

type ListingStatus = "pending" | "approved" | "rejected";

function normalizedType(type?: string | null) {
  return String(type || "").trim().toLowerCase();
}

function isEventLike(type?: string | null) {
  const clean = normalizedType(type);
  return ["eventcentre", "event centre", "event center", "event-centre", "conference hall", "open space", "open-space"].includes(clean);
}

function getPriceSuffix(type?: string | null) {
  return isEventLike(type) ? "/event" : "/night";
}

function getInventoryLabel(type?: string | null) {
  const clean = normalizedType(type);
  if (clean === "apartment") return "Unit Inventory";
  if (clean === "conference hall") return "Hall Inventory";
  if (clean === "open space" || clean === "open-space") return "Space Inventory";
  if (clean === "eventcentre" || clean === "event centre" || clean === "event center" || clean === "event-centre") return "Space Inventory";
  if (clean === "resort") return "Accommodation Inventory";
  return "Room Inventory";
}

function getInventoryUnit(type?: string | null) {
  const clean = normalizedType(type);
  if (clean === "apartment") return "unit";
  if (clean === "conference hall") return "hall";
  if (clean === "open space" || clean === "open-space") return "space";
  if (clean === "eventcentre" || clean === "event centre" || clean === "event center" || clean === "event-centre") return "space";
  if (clean === "resort") return "option";
  return "room";
}

interface ModeratedListing extends VenueResponse {
  moderationStatus: ListingStatus;
  submittedAt: string;
  moderatedAt?: string;
  moderatorNote?: string;
}

function ModeratorPage() {
  const { user } = useAuth();
  const [listings, setListings] = useState<ModeratedListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<ListingStatus | "all">("all");
  const [selected, setSelected] = useState<ModeratedListing | null>(null);
  const [selectedRooms, setSelectedRooms] = useState<RoomTypeResponse[]>([]);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [note, setNote] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  const toListingStatus = (status?: string | null): ListingStatus =>
    status === "approved" || status === "rejected" ? status : "pending";

  const refreshListings = async () => {
    setLoading(true);
    try {
      const res = await venuesApi.moderationList({ pageSize: 100 });
      const mapped: ModeratedListing[] = res.data.map((v) => ({
        ...v,
        moderationStatus: toListingStatus(v.moderationStatus),
        submittedAt: (v as any).submittedAt ?? (v as any).createdAt ?? new Date().toISOString(),
      }));
      setListings(mapped);
    } catch {
      toast.error("Failed to load listings");
    } finally {
      setLoading(false);
    }
  };

  /* Load venues using their real backend moderation status. */
  useEffect(() => {
    void refreshListings();
  }, []);

  useEffect(() => {
    if (!selected) {
      setSelectedRooms([]);
      return;
    }

    setLoadingRooms(true);
    roomTypesApi.list(selected.id)
      .then(setSelectedRooms)
      .catch(() => setSelectedRooms([]))
      .finally(() => setLoadingRooms(false));
  }, [selected?.id]);

  const filtered = useMemo(() => {
    return listings.filter(l => {
      const matchesSearch =
        l.name.toLowerCase().includes(search.toLowerCase()) ||
        l.location.toLowerCase().includes(search.toLowerCase());
      const matchesFilter = filter === "all" || l.moderationStatus === filter;
      return matchesSearch && matchesFilter;
    });
  }, [listings, search, filter]);

  const stats = useMemo(() => ({
    pending: listings.filter(l => l.moderationStatus === "pending").length,
    approved: listings.filter(l => l.moderationStatus === "approved").length,
    rejected: listings.filter(l => l.moderationStatus === "rejected").length,
  }), [listings]);

  const handleApprove = async (id: string) => {
    setActionLoading(true);
    try {
      const res = await venuesApi.approve(id, note || undefined);
      setListings(prev => prev.map(l =>
        l.id === id ? { ...l, moderationStatus: "approved", status: "active", moderatedAt: res.moderatedAt ?? new Date().toISOString(), moderatorNote: (res.moderatorNote ?? note) || undefined } : l
      ));
      toast.success("Listing approved and is now live on the site.");
      setSelected(null);
      setNote("");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to approve listing");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (id: string) => {
    setActionLoading(true);
    try {
      const res = await venuesApi.reject(id, note || undefined);
      setListings(prev => prev.map(l =>
        l.id === id ? { ...l, moderationStatus: "rejected", status: "inactive", moderatedAt: res.moderatedAt ?? new Date().toISOString(), moderatorNote: (res.moderatorNote ?? note) || undefined } : l
      ));
      toast.success("Listing rejected. It will not be shown publicly.");
      setSelected(null);
      setNote("");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to reject listing");
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl space-y-3 p-2.5 sm:p-4 lg:p-5">
      {/* Header */}
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            <Link to="/admin/dashboard" className="flex items-center gap-1 hover:text-primary transition-colors">
              <ArrowLeft className="size-3" /> Dashboard
            </Link>
            <span>/</span>
            <span className="text-foreground">Moderation</span>
          </div>
          <h1 className="mt-0.5 font-display text-lg font-bold tracking-tight sm:text-xl">
            Listing Moderation
          </h1>
          <p className="mt-0.5 text-xs text-muted-foreground sm:text-sm">
            Review and approve property listings before they go public.
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-2 sm:grid-cols-3">
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-2.5 sm:p-3">
          <div className="flex items-center gap-2 text-amber-700">
            <Clock className="size-4" />
            <span className="text-sm font-semibold">Pending Review</span>
          </div>
          <p className="mt-1.5 text-xl font-bold text-amber-800">{stats.pending}</p>
        </div>
        <div className="rounded-xl border border-green-200 bg-green-50 p-2.5 sm:p-3">
          <div className="flex items-center gap-2 text-green-700">
            <CheckCircle2 className="size-4" />
            <span className="text-sm font-semibold">Approved</span>
          </div>
          <p className="mt-1.5 text-xl font-bold text-green-800">{stats.approved}</p>
        </div>
        <div className="rounded-xl border border-red-200 bg-red-50 p-2.5 sm:p-3">
          <div className="flex items-center gap-2 text-red-700">
            <XCircle className="size-4" />
            <span className="text-sm font-semibold">Rejected</span>
          </div>
          <p className="mt-1.5 text-xl font-bold text-red-800">{stats.rejected}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
          <Input
            placeholder="Search listings by name or location..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="h-9 rounded-xl pl-9"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {(["all", "pending", "approved", "rejected"] as const).map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`rounded-full px-2.5 py-1 text-xs sm:text-sm font-medium transition-colors ${
                filter === f
                  ? "bg-primary text-primary-foreground"
                  : "border border-border bg-background text-muted-foreground hover:bg-accent"
              }`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Listings Table */}
      {loading ? (
        <div className="flex items-center justify-center py-14">
          <Loader2 className="size-7 animate-spin text-primary" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-border py-10 text-center">
          <Gavel className="mb-2 size-10 text-muted-foreground/30" />
          <p className="font-semibold">No listings found</p>
          <p className="mt-1 text-sm text-muted-foreground">Try adjusting your search or filter.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(listing => (
            <div
              key={listing.id}
              className="flex flex-col gap-2.5 rounded-xl border border-border/60 bg-card p-2.5 shadow-sm sm:flex-row sm:items-center"
            >
              <img
                src={resolveMediaUrl(listing.heroImage)}
                alt={listing.name}
                className="aspect-[4/3] w-full rounded-xl object-cover sm:w-24 sm:aspect-square"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-display text-sm font-semibold truncate">{listing.name}</h3>
                  <Badge variant="outline" className={
                    listing.moderationStatus === "pending" ? "border-amber-200 bg-amber-50 text-amber-700" :
                    listing.moderationStatus === "approved" ? "border-green-200 bg-green-50 text-green-700" :
                    "border-red-200 bg-red-50 text-red-700"
                  }>
                    {listing.moderationStatus}
                  </Badge>
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><MapPin className="size-3.5" /> {listing.location}</span>
                  <span className="flex items-center gap-1"><Building2 className="size-3.5" /> {listing.type}</span>
                </div>
                <div className="mt-1 text-sm font-medium">{formatNaira(listing.basePrice)} <span className="text-muted-foreground font-normal">{getPriceSuffix(listing.type)}</span></div>
                {listing.moderatorNote && (
                  <p className="mt-1 text-xs text-muted-foreground italic">Note: {listing.moderatorNote}</p>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={() => setSelected(listing)}
                  className="grid size-8 place-items-center rounded-lg border border-border bg-background hover:bg-accent transition-colors"
                  title="View details"
                >
                  <Eye className="size-4" />
                </button>
                {listing.moderationStatus !== "approved" && (
                    <Button
                      size="sm"
                      className="h-7 gap-1 rounded-lg bg-green-600 hover:bg-green-700 text-white"
                      onClick={() => handleApprove(listing.id)}
                      disabled={actionLoading}
                    >
                      <CheckCircle2 className="size-4" /> Approve
                    </Button>
                )}
                {listing.moderationStatus !== "rejected" && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 gap-1 rounded-lg border-red-200 text-red-600 hover:bg-red-50"
                      onClick={() => handleReject(listing.id)}
                      disabled={actionLoading}
                    >
                      <XCircle className="size-4" /> {listing.moderationStatus === "approved" ? "Revoke Approval" : "Reject"}
                    </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm" onClick={() => setSelected(null)} />
          <div className="relative w-full max-w-lg max-h-[calc(100dvh-2rem)] overflow-hidden rounded-3xl border border-border/60 bg-card shadow-elegant">
            <div className="max-h-[calc(100dvh-2rem)] overflow-y-auto p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-xl font-bold">Review Listing</h2>
                <button onClick={() => setSelected(null)} className="grid size-8 place-items-center rounded-lg hover:bg-accent"><XCircle className="size-4" /></button>
              </div>
              <img
                src={resolveMediaUrl(selected.heroImage)}
                alt={selected.name}
                className="w-full aspect-video rounded-xl object-cover mb-4"
              />
              <h3 className="font-display text-lg font-semibold">{selected.name}</h3>
              <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1"><MapPin className="size-3.5" /> {selected.location}</p>
              <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1"><Building2 className="size-3.5" /> {selected.type} &middot; {formatNaira(selected.basePrice)}{getPriceSuffix(selected.type)}</p>

              <div className="mt-4 rounded-xl border border-border/60 bg-muted/20 p-3">
                <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{getInventoryLabel(selected.type)}</p>
                {loadingRooms ? (
                  <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                    <Loader2 className="size-3.5 animate-spin" /> Loading room types...
                  </div>
                ) : selectedRooms.length === 0 ? (
                  <p className="mt-2 text-sm text-muted-foreground">No {getInventoryUnit(selected.type)} types for this listing.</p>
                ) : (
                  <div className="mt-2 space-y-2">
                    {selectedRooms.map(room => (
                      <div key={room.id} className="flex items-center justify-between gap-3 rounded-lg bg-background px-3 py-2 text-sm">
                        <div>
                          <p className="font-semibold">{room.name}</p>
                          <p className="text-xs text-muted-foreground">{room.capacity} guests &middot; {formatNaira(room.pricePerDay)}{getPriceSuffix(selected.type)}</p>
                        </div>
                        <span className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-bold text-primary">
                          {room.quantity} {getInventoryUnit(selected.type)}{room.quantity !== 1 ? "s" : ""}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="mt-4 space-y-2">
                <Label className="text-sm font-bold">Moderator note (optional)</Label>
                <textarea
                  value={note}
                  onChange={e => setNote(e.target.value)}
                  placeholder="Add a note about why this listing is approved or rejected..."
                  className="w-full rounded-xl border border-border/80 bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                  rows={3}
                />
              </div>

              <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                {selected.moderationStatus !== "approved" && (
                  <Button
                    className="flex-1 gap-1 bg-green-600 hover:bg-green-700 text-white"
                    onClick={() => handleApprove(selected.id)}
                    disabled={actionLoading}
                  >
                    <CheckCircle2 className="size-4" /> Approve
                  </Button>
                )}
                {selected.moderationStatus !== "rejected" && (
                  <Button
                    variant="outline"
                    className="flex-1 gap-1 border-red-200 text-red-600 hover:bg-red-50"
                    onClick={() => handleReject(selected.id)}
                    disabled={actionLoading}
                  >
                    <XCircle className="size-4" /> {selected.moderationStatus === "approved" ? "Revoke Approval" : "Reject"}
                  </Button>
                )}
                <Button variant="outline" className="flex-1" onClick={() => setSelected(null)}>Close</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
